import React, { useState } from "react";
import {
  Eye,
  Trash2,
  X,
  Plus,
  Edit2,
  Clock,
  DollarSign,
  Upload,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Calendar,
  Users,
  Tag,
  Loader2,
} from "lucide-react";
import {
  useGetServicesQuery,
  useCreateServiceMutation,
  useUpdateServiceMutation,
  useDeleteServiceMutation,
  useUploadImagesMutation,
} from "../store/api/adminApi";
import { useSelector } from "react-redux";
import { useToast } from "../components/Toast";

const PAGE_SIZE = 10;

function genId() {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function to12(value) {
  if (!value) return "";
  const [h, m] = value.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  const hour = h % 12 || 12;
  return `${String(hour).padStart(2, "0")}:${String(m).padStart(2, "0")} ${ampm}`;
}

function to24(value) {
  if (!value) return "";
  const [time, ampm] = value.split(" ");
  if (!ampm) return value;
  let [h, m] = time.split(":").map(Number);
  if (ampm === "PM" && h !== 12) h += 12;
  if (ampm === "AM" && h === 12) h = 0;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

/* ─── Field ─────────────────────────────────────────────── */
function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[12px] font-manrope-semibold text-gray-600 mb-1.5">
        {label}
      </label>
      {children}
    </div>
  );
}

function Input({ ...props }) {
  return (
    <input
      {...props}
      className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-100 rounded-xl text-[13px] font-manrope-regular text-gray-700 placeholder-gray-400 focus:outline-none focus:border-primary transition-all"
    />
  );
}

/* ─── Image upload panel ─────────────────────────────────── */
function ImageUploadPanel({ imageUrls, onUpload, isUploading }) {
  const ref = React.useRef();

  const handleFiles = (files) => {
    const fd = new FormData();
    Array.from(files).forEach((f) => fd.append("images", f));
    onUpload(fd);
  };

  const bannerUrl = imageUrls[0] || null;
  const thumbUrls = imageUrls.slice(1, 4);

  return (
    <div className="flex flex-col gap-2">
      <div
        onClick={() => !isUploading && ref.current.click()}
        className="w-full aspect-[16/9] bg-gray-100 rounded-xl flex items-center justify-center cursor-pointer hover:bg-gray-200 transition-all overflow-hidden relative"
      >
        {bannerUrl ? (
          <img src={bannerUrl} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="flex flex-col items-center gap-1.5">
            {isUploading ? (
              <Loader2 size={22} className="text-gray-400 animate-spin" />
            ) : (
              <Upload size={22} className="text-gray-400" strokeWidth={1.6} />
            )}
            <span className="text-[11px] font-manrope-regular text-gray-400 text-center px-4">
              {isUploading ? "Uploading…" : "Upload images (first = banner)"}
            </span>
          </div>
        )}
        {isUploading && bannerUrl && (
          <div className="absolute inset-0 bg-white/60 flex items-center justify-center">
            <Loader2 size={22} className="text-primary animate-spin" />
          </div>
        )}
      </div>

      <div className="flex gap-2">
        {[0].map((i) => (
          <div
            key={i}
            onClick={() => !isUploading && ref.current.click()}
            className="w-[72px] h-[72px] bg-gray-100 rounded-xl flex items-center justify-center cursor-pointer hover:bg-gray-200 transition-all overflow-hidden flex-shrink-0"
          >
            {thumbUrls[i] ? (
              <img
                src={thumbUrls[i]}
                alt=""
                className="w-full h-full object-cover"
              />
            ) : (
              <Upload size={14} className="text-gray-400" strokeWidth={1.6} />
            )}
          </div>
        ))}
        <button
          type="button"
          onClick={() => ref.current.click()}
          className="w-[72px] h-[72px] bg-primary/10 rounded-xl flex items-center justify-center hover:bg-primary/20 transition-all flex-shrink-0"
        >
          <Plus size={16} className="text-primary" strokeWidth={2} />
        </button>
      </div>

      <input
        ref={ref}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        multiple
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
      />
    </div>
  );
}

/* ─── Service modal ──────────────────────────────────────── */
function ServiceModal({ mode = "add", service = null, onClose }) {
  const isEdit = mode === "edit";

  const [form, setForm] = useState({
    title: isEdit ? service?.title || "" : "",
    description: isEdit ? service?.description || "" : "",
    price: isEdit ? String(service?.price || "") : "",
    carsQuantity: isEdit
      ? String(service?.carsQuantity || service?.availableSpots || "1")
      : "1",
    discountLabel: isEdit ? service?.discountLabel || "" : "",
    isActive: isEdit ? service?.isActive !== false : true,
  });

  // Manual time slots: [{id, startTime (24h internal), endTime (24h internal)}]
  const [timeSlots, setTimeSlots] = useState(
    isEdit
      ? (service?.timeSlots || []).map((s) => ({
          id: s.id,
          startTime: to24(s.startTime),
          endTime: to24(s.endTime),
        }))
      : [],
  );
  const [newSlotStart, setNewSlotStart] = useState("");
  const [newSlotEnd, setNewSlotEnd] = useState("");
  const [slotError, setSlotError] = useState("");

  // Absence dates
  const [absenceDates, setAbsenceDates] = useState(
    isEdit ? service?.absenceDates || [] : [],
  );
  const [newAbsenceDate, setNewAbsenceDate] = useState("");

  const [imageUrls, setImageUrls] = useState(
    isEdit ? service?.images || [] : [],
  );
  const [error, setError] = useState("");

  const set = (key) => (e) =>
    setForm((f) => ({
      ...f,
      [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value,
    }));

  const [createService, { isLoading: isCreating }] = useCreateServiceMutation();
  const [updateService, { isLoading: isUpdating }] = useUpdateServiceMutation();
  const [uploadImages, { isLoading: isUploading }] = useUploadImagesMutation();
  const toast = useToast();

  const isSaving = isCreating || isUpdating;

  const handleImageUpload = async (formData) => {
    try {
      const res = await uploadImages(formData).unwrap();
      setImageUrls((prev) => [
        ...prev,
        ...res.data.images.map((img) => img.url),
      ]);
      toast.success("Images uploaded successfully.");
    } catch (err) {
      toast.error(
        "Image upload failed: " +
          (err?.data?.error?.message || "Unknown error"),
      );
    }
  };

  const addSlot = () => {
    setSlotError("");
    if (!newSlotStart || !newSlotEnd) {
      setSlotError("Both start and end time are required.");
      return;
    }
    if (newSlotStart >= newSlotEnd) {
      setSlotError("End time must be after start time.");
      return;
    }
    const duplicate = timeSlots.some(
      (s) => s.startTime === newSlotStart && s.endTime === newSlotEnd,
    );
    if (duplicate) {
      setSlotError("This slot already exists.");
      return;
    }
    setTimeSlots((prev) => [
      ...prev,
      { id: genId(), startTime: newSlotStart, endTime: newSlotEnd },
    ]);
    setNewSlotStart("");
    setNewSlotEnd("");
  };

  const removeSlot = (idx) =>
    setTimeSlots((prev) => prev.filter((_, i) => i !== idx));

  const addAbsenceDate = () => {
    if (!newAbsenceDate) return;
    if (absenceDates.includes(newAbsenceDate)) return;
    setAbsenceDates((prev) => [...prev, newAbsenceDate].sort());
    setNewAbsenceDate("");
  };

  const removeAbsenceDate = (date) =>
    setAbsenceDates((prev) => prev.filter((d) => d !== date));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!form.title.trim() || !form.description.trim() || !form.price) {
      setError("Title, description and price are required.");
      return;
    }

    const body = {
      title: form.title.trim(),
      category: "General",
      description: form.description.trim(),
      price: Number(form.price),
      carsQuantity: Number(form.carsQuantity) || 1,
      availableSpots: Number(form.carsQuantity) || 1,
      isActive: form.isActive,
      images: imageUrls,
      timeSlots: timeSlots.map((s) => ({
        id: s.id,
        startTime: to12(s.startTime),
        endTime: to12(s.endTime),
      })),
      absenceDates,
    };

    if (form.discountLabel.trim())
      body.discountLabel = form.discountLabel.trim();

    try {
      if (isEdit) {
        await updateService({
          id: service._id || service.id,
          ...body,
        }).unwrap();
        toast.success("Service updated successfully.");
      } else {
        await createService(body).unwrap();
        toast.success("Service created successfully.");
      }
      onClose();
    } catch (err) {
      toast.error(err?.data?.error?.message || "Failed to save service.");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative bg-white rounded-2xl shadow-2xl w-[800px] max-h-[92vh] overflow-y-auto flex">
        {/* Left – images */}
        <div className="w-[220px] flex-shrink-0 p-5 border-r border-gray-100">
          <p className="text-[11px] font-manrope-semibold text-gray-400 uppercase tracking-wider mb-3">
            Images
          </p>
          <ImageUploadPanel
            imageUrls={imageUrls}
            onUpload={handleImageUpload}
            isUploading={isUploading}
          />
          {imageUrls.length > 0 && (
            <button
              type="button"
              onClick={() => setImageUrls([])}
              className="mt-2 text-[11px] text-red-400 hover:text-red-600 font-manrope-regular transition-all"
            >
              Clear all images
            </button>
          )}
        </div>

        {/* Right – form */}
        <form
          onSubmit={handleSubmit}
          className="flex-1 p-6 flex flex-col gap-0"
        >
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-[16px] font-manrope-bold text-gray-900">
              {isEdit ? "Edit service" : "Add new service"}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="w-7 h-7 rounded-full hover:bg-gray-100 flex items-center justify-center transition-all"
            >
              <X size={15} className="text-gray-500" />
            </button>
          </div>

          <div className="flex flex-col gap-3.5 flex-1">
            {/* Title */}
            <Field label="Title *">
              <Input
                placeholder="e.g. Deep cleaning for apartment"
                value={form.title}
                onChange={set("title")}
              />
            </Field>

            {/* Description */}
            <Field label="Description *">
              <textarea
                placeholder="Describe what the service includes…"
                value={form.description}
                onChange={set("description")}
                rows={3}
                className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-100 rounded-xl text-[13px] font-manrope-regular text-gray-700 placeholder-gray-400 focus:outline-none focus:border-primary transition-all resize-none"
              />
            </Field>

            {/* Price + Cars */}
            <div className="grid grid-cols-2 gap-3">
              <Field label="Price (USD) *">
                <div className="relative">
                  <Input
                    type="number"
                    min="0"
                    placeholder="0.00"
                    value={form.price}
                    onChange={set("price")}
                  />
                  <DollarSign
                    size={13}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400"
                    strokeWidth={1.6}
                  />
                </div>
              </Field>
              <Field label="Cars quantity">
                <div className="relative">
                  <Input
                    type="number"
                    min="1"
                    placeholder="1"
                    value={form.carsQuantity}
                    onChange={set("carsQuantity")}
                  />
                  <Users
                    size={13}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400"
                    strokeWidth={1.6}
                  />
                </div>
              </Field>
            </div>

            {/* Discount label */}
            <Field label="Discount label (optional)">
              <div className="relative">
                <Input
                  placeholder="e.g. 10% off with coupon"
                  value={form.discountLabel}
                  onChange={set("discountLabel")}
                />
                <Tag
                  size={13}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400"
                  strokeWidth={1.6}
                />
              </div>
            </Field>

            {/* ── Time slots ── */}
            <div className="border border-gray-100 rounded-xl p-4 flex flex-col gap-3">
              <div className="flex items-center gap-2">
                <Clock size={13} className="text-gray-400" strokeWidth={1.6} />
                <p className="text-[12px] font-manrope-semibold text-gray-500 uppercase tracking-wider">
                  Time slots
                </p>
              </div>

              {timeSlots.length > 0 && (
                <div className="flex flex-col gap-1.5">
                  {timeSlots.map((slot, i) => (
                    <div
                      key={slot.id}
                      className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2"
                    >
                      <span className="text-[12px] font-manrope-semibold text-gray-700">
                        {to12(slot.startTime)} – {to12(slot.endTime)}
                      </span>
                      <button
                        type="button"
                        onClick={() => removeSlot(i)}
                        className="w-5 h-5 rounded-full hover:bg-gray-200 flex items-center justify-center transition-all"
                      >
                        <X size={11} className="text-gray-400" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="grid grid-cols-[1fr_1fr_auto] gap-2 items-end">
                <Field label="Start time">
                  <Input
                    type="time"
                    value={newSlotStart}
                    onChange={(e) => {
                      setNewSlotStart(e.target.value);
                      setSlotError("");
                    }}
                  />
                </Field>
                <Field label="End time">
                  <Input
                    type="time"
                    value={newSlotEnd}
                    onChange={(e) => {
                      setNewSlotEnd(e.target.value);
                      setSlotError("");
                    }}
                  />
                </Field>
                <button
                  type="button"
                  onClick={addSlot}
                  className="h-[38px] px-3 bg-primary text-white rounded-xl text-[12px] font-manrope-semibold hover:opacity-90 transition-all flex items-center gap-1.5 whitespace-nowrap"
                >
                  <Plus size={13} strokeWidth={2.2} />
                  Add slot
                </button>
              </div>

              {slotError && (
                <p className="text-[11px] text-red-500 font-manrope-regular">
                  {slotError}
                </p>
              )}
              {timeSlots.length === 0 && !slotError && (
                <p className="text-[11px] font-manrope-regular text-gray-400">
                  No time slots added yet. Add at least one slot so customers
                  can book.
                </p>
              )}
            </div>

            {/* ── Absence dates ── */}
            <div className="border border-gray-100 rounded-xl p-4 flex flex-col gap-3">
              <div className="flex items-center gap-2">
                <Calendar
                  size={13}
                  className="text-gray-400"
                  strokeWidth={1.6}
                />
                <p className="text-[12px] font-manrope-semibold text-gray-500 uppercase tracking-wider">
                  Absence dates
                </p>
                <span className="text-[11px] font-manrope-regular text-gray-400">
                  (bookings disabled on these dates)
                </span>
              </div>

              {absenceDates.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {absenceDates.map((d) => (
                    <div
                      key={d}
                      className="flex items-center gap-1 bg-primary/10 text-primary text-[11px] font-manrope-semibold px-2.5 py-1 rounded-full"
                    >
                      {d}
                      <button
                        type="button"
                        onClick={() => removeAbsenceDate(d)}
                        className="w-3.5 h-3.5 rounded-full hover:bg-primary/20 flex items-center justify-center ml-0.5"
                      >
                        <X size={9} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex gap-2 items-end">
                <div className="flex-1">
                  <Field label="Select date">
                    <Input
                      type="date"
                      value={newAbsenceDate}
                      onChange={(e) => setNewAbsenceDate(e.target.value)}
                    />
                  </Field>
                </div>
                <button
                  type="button"
                  onClick={addAbsenceDate}
                  disabled={!newAbsenceDate}
                  className="h-[38px] px-3 bg-primary/10 text-primary rounded-xl text-[12px] font-manrope-semibold hover:bg-primary/20 disabled:opacity-40 transition-all flex items-center gap-1.5 whitespace-nowrap"
                >
                  <Plus size={13} strokeWidth={2.2} />
                  Add date
                </button>
              </div>

              {absenceDates.length === 0 && (
                <p className="text-[11px] font-manrope-regular text-gray-400">
                  No absence dates set. Service will be available every day.
                </p>
              )}
            </div>

            {/* Active toggle */}
            <div className="flex items-center gap-3">
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.isActive}
                  onChange={set("isActive")}
                  className="sr-only peer"
                />
                <div className="w-9 h-5 bg-gray-200 rounded-full peer peer-checked:bg-primary transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-4" />
              </label>
              <span className="text-[13px] font-manrope-regular text-gray-600">
                Active — visible to customers
              </span>
            </div>

            {error && (
              <p className="text-[12px] font-manrope-regular text-red-500 bg-red-50 px-4 py-2.5 rounded-xl">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={isSaving}
              className="mt-1 w-full py-3 bg-primary rounded-xl text-white text-[14px] font-manrope-semibold hover:opacity-90 disabled:opacity-60 transition-all flex items-center justify-center gap-2 mb-4"
            >
              {isSaving && <Loader2 size={15} className="animate-spin" />}
              {isSaving
                ? "Saving…"
                : isEdit
                  ? "Save changes"
                  : "Create service"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ─── Delete modal ───────────────────────────────────────── */
function DeleteModal({ service, onCancel, onConfirm, isDeleting }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onCancel}
      />
      <div className="relative bg-white rounded-2xl p-8 w-[340px] flex flex-col items-center text-center shadow-2xl">
        <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mb-4">
          <Trash2 size={28} className="text-primary" strokeWidth={1.8} />
        </div>
        <h3 className="text-[16px] font-manrope-bold text-primary mb-2">
          Delete service?
        </h3>
        <p className="text-[12px] font-manrope-regular text-gray-400 mb-1 leading-relaxed">
          You are about to delete{" "}
          <span className="font-manrope-semibold text-gray-700">
            {service?.title}
          </span>
          .
        </p>
        <div className="flex gap-3 w-full mt-5">
          <button
            onClick={onCancel}
            className="flex-1 py-2.5 rounded-xl border border-gray-200 text-[13px] font-manrope-semibold text-gray-600 hover:bg-gray-50 transition-all"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isDeleting}
            className="flex-1 py-2.5 rounded-xl bg-primary text-white text-[13px] font-manrope-semibold hover:opacity-90 disabled:opacity-60 transition-all flex items-center justify-center gap-2"
          >
            {isDeleting && <Loader2 size={13} className="animate-spin" />}
            {isDeleting ? "Deleting…" : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Service details panel ──────────────────────────────── */
function ServiceDetailsPanel({ service, onClose, onEdit, onDelete }) {
  const banner = service?.images?.[0] || null;
  const thumbs = service?.images?.slice(1, 4) || [];

  return (
    <div className="w-[300px] bg-white border-l border-gray-100 flex flex-col fixed right-0 top-0 h-full z-40 shadow-xl overflow-y-auto">
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
        <h2 className="text-[15px] font-manrope-bold text-gray-900">
          Service details
        </h2>
        <button
          onClick={onClose}
          className="w-7 h-7 rounded-full hover:bg-gray-100 flex items-center justify-center transition-all"
        >
          <X size={15} className="text-gray-500" />
        </button>
      </div>

      <div className="flex-1 px-5 py-5 flex flex-col gap-4">
        {/* Banner */}
        <div className="w-full aspect-[16/10] rounded-xl overflow-hidden bg-gray-100">
          {banner ? (
            <img
              src={banner}
              alt="service"
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-300">
              <Upload size={32} strokeWidth={1.2} />
            </div>
          )}
        </div>

        {/* Title + category */}
        <div>
          <h3 className="text-[14px] font-manrope-bold text-gray-900">
            {service.title}
          </h3>
          <span className="text-[11px] font-manrope-regular text-primary bg-primary/10 px-2 py-0.5 rounded-full">
            {service.category}
          </span>
        </div>

        {/* Description */}
        <p className="text-[12px] font-manrope-regular text-gray-500 leading-relaxed">
          {service.description}
        </p>

        {/* Thumbnails */}
        {thumbs.length > 0 && (
          <div className="flex gap-2">
            {thumbs.map((src, i) => (
              <div
                key={i}
                className="flex-1 aspect-square rounded-lg overflow-hidden bg-gray-100"
              >
                <img src={src} alt="" className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
        )}

        {/* Stats */}
        <div className="flex gap-3">
          <div className="flex-1 bg-gray-50 rounded-xl p-3 text-center">
            <div className="flex items-center justify-center gap-1 mb-0.5">
              <DollarSign
                size={10}
                className="text-gray-400"
                strokeWidth={1.6}
              />
              <p className="text-[10px] font-manrope-regular text-gray-400">
                Price
              </p>
            </div>
            <p className="text-[15px] font-manrope-bold text-gray-900">
              ${service.price}
            </p>
          </div>
          <div className="flex-1 bg-gray-50 rounded-xl p-3 text-center">
            <div className="flex items-center justify-center gap-1 mb-0.5">
              <Users size={10} className="text-gray-400" strokeWidth={1.6} />
              <p className="text-[10px] font-manrope-regular text-gray-400">
                Cars
              </p>
            </div>
            <p className="text-[15px] font-manrope-bold text-gray-900">
              {service.carsQuantity || service.availableSpots || 1}
            </p>
          </div>
          <div className="flex-1 bg-gray-50 rounded-xl p-3 text-center">
            <div className="flex items-center justify-center gap-1 mb-0.5">
              <Clock size={10} className="text-gray-400" strokeWidth={1.6} />
              <p className="text-[10px] font-manrope-regular text-gray-400">
                Slots
              </p>
            </div>
            <p className="text-[15px] font-manrope-bold text-gray-900">
              {service.timeSlots?.length || 0}
            </p>
          </div>
        </div>

        {/* Time slots list */}
        {service.timeSlots?.length > 0 && (
          <div>
            <p className="text-[11px] font-manrope-semibold text-gray-400 uppercase tracking-wide mb-2">
              Time slots
            </p>
            <div className="flex flex-col gap-1">
              {service.timeSlots.map((s) => (
                <div
                  key={s.id}
                  className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-1.5"
                >
                  <Clock size={11} className="text-primary" strokeWidth={1.6} />
                  <span className="text-[12px] font-manrope-semibold text-gray-700">
                    {s.startTime} – {s.endTime}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Absence dates */}
        {service.absenceDates?.length > 0 && (
          <div>
            <p className="text-[11px] font-manrope-semibold text-gray-400 uppercase tracking-wide mb-2">
              Absence dates
            </p>
            <div className="flex flex-wrap gap-1.5">
              {service.absenceDates.map((d) => (
                <span
                  key={d}
                  className="text-[11px] font-manrope-semibold bg-primary/10 text-primary px-2.5 py-1 rounded-full"
                >
                  {d}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Discount */}
        {service.discountLabel && (
          <div className="flex items-center gap-2 bg-yellow-50 rounded-xl px-3 py-2">
            <Tag size={12} className="text-yellow-500" strokeWidth={1.6} />
            <span className="text-[12px] font-manrope-regular text-yellow-700">
              {service.discountLabel}
            </span>
          </div>
        )}

        {/* Active badge */}
        <div
          className={`text-center py-1.5 rounded-xl text-[11px] font-manrope-semibold ${service.isActive ? "bg-green-50 text-green-600" : "bg-gray-100 text-gray-400"}`}
        >
          {service.isActive ? "Active" : "Inactive"}
        </div>

        {/* Actions */}
        <div className="flex gap-2 mt-auto">
          <button
            onClick={onEdit}
            className="flex-1 py-2.5 rounded-xl border border-gray-200 text-[12px] font-manrope-semibold text-gray-700 hover:bg-gray-50 transition-all"
          >
            Edit
          </button>
          <button
            onClick={onDelete}
            className="flex-1 py-2.5 rounded-xl bg-primary text-white text-[12px] font-manrope-semibold hover:opacity-90 transition-all"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Main Page ──────────────────────────────────────────── */
export default function Services() {
  const user = useSelector((state) => state.auth.user);
  const [search, setSearch] = useState("");
  const [selectedService, setSelectedService] = useState(null);
  const [modal, setModal] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const { data, isLoading, isError } = useGetServicesQuery();
  const [deleteService, { isLoading: isDeleting }] = useDeleteServiceMutation();
  const toast = useToast();

  const services = data?.data || [];
  const filtered = services.filter((s) =>
    s.title?.toLowerCase().includes(search.toLowerCase()),
  );
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE,
  );

  const handleDelete = async () => {
    try {
      await deleteService(deleteTarget._id || deleteTarget.id).unwrap();
      setDeleteTarget(null);
      if (selectedService?.id === deleteTarget.id) setSelectedService(null);
      toast.success("Service deleted successfully.");
    } catch {
      toast.error("Failed to delete service.");
    }
  };

  return (
    <>
      <div
        className={`flex flex-col min-h-screen transition-all ${selectedService ? "mr-[300px]" : ""}`}
      >
        {/* Header */}
        <header className="flex items-center justify-between px-8 pt-7 pb-4">
          <div>
            <h1 className="text-[22px] font-manrope-bold text-gray-900">
              Service management
            </h1>
            <p className="text-[12px] font-manrope-regular text-gray-400 mt-0.5">
              Manage all services available on the booking platform.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-gray-200 flex items-center justify-center">
              <span className="text-sm font-manrope-bold text-gray-600">
                {user?.name?.[0]?.toUpperCase() || "A"}
              </span>
            </div>
            <div className="flex flex-col">
              <span className="text-[13px] font-manrope-semibold text-gray-900 leading-tight">
                {user?.name}
              </span>
              <span className="text-[11px] font-manrope-regular text-gray-400 leading-tight">
                {user?.email}
              </span>
            </div>
          </div>
        </header>

        <div className="flex-1 px-8 pb-8">
          {/* Toolbar */}
          <div className="flex items-center gap-3 mb-5">
            <input
              type="text"
              placeholder="Search service"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full max-w-xs pl-4 pr-4 py-2.5 bg-white border border-gray-100 rounded-xl text-[13px] font-manrope-regular text-gray-700 placeholder-gray-400 focus:outline-none focus:border-primary transition-all"
            />
            <button
              onClick={() => setModal("add")}
              className="flex items-center gap-2 px-4 py-2.5 bg-primary rounded-xl text-white text-[13px] font-manrope-semibold hover:opacity-90 transition-all ml-auto"
            >
              <Plus size={15} strokeWidth={2.2} />
              Add new service
            </button>
          </div>

          {/* Table */}
          <div className="bg-white rounded-2xl overflow-hidden">
            <div className="grid grid-cols-[2.5fr_1fr_1fr_1fr_1fr] px-5 py-3 border-b border-gray-50">
              {["Name", "Slots", "Cars", "Price", "Action"].map((col) => (
                <span
                  key={col}
                  className="text-[12px] font-manrope-semibold text-gray-400"
                >
                  {col}
                </span>
              ))}
            </div>

            {isLoading && (
              <div className="flex items-center justify-center py-16 gap-2 text-gray-400">
                <Loader2 size={18} className="animate-spin" />
                <span className="text-[13px] font-manrope-regular">
                  Loading services…
                </span>
              </div>
            )}
            {isError && (
              <div className="flex items-center justify-center py-16 text-red-400 text-[13px] font-manrope-regular">
                Failed to load services. Please try again.
              </div>
            )}
            {!isLoading && !isError && paginated.length === 0 && (
              <div className="flex items-center justify-center py-16 text-gray-400 text-[13px] font-manrope-regular">
                No services found.
              </div>
            )}

            {paginated.map((svc) => (
              <div
                key={svc._id}
                className="grid grid-cols-[2.5fr_1fr_1fr_1fr_1fr] px-5 py-3 border-b border-gray-50 hover:bg-gray-50/50 transition-all items-center"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0 flex items-center justify-center">
                    {svc.images?.[0] ? (
                      <img
                        src={svc.images[0]}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <Upload
                        size={14}
                        className="text-gray-300"
                        strokeWidth={1.4}
                      />
                    )}
                  </div>
                  <div className="flex flex-col">
                    <span className="text-[13px] font-manrope-semibold text-gray-800 leading-tight">
                      {svc.title}
                    </span>
                    <span className="text-[11px] font-manrope-regular text-gray-400">
                      {svc.category}
                    </span>
                  </div>
                </div>
                <span className="text-[12px] font-manrope-regular text-gray-500">
                  {svc.timeSlots?.length || 0}
                </span>
                <span className="text-[12px] font-manrope-regular text-gray-500">
                  {svc.carsQuantity || svc.availableSpots || 1}
                </span>
                <span className="text-[12px] font-manrope-regular text-gray-500">
                  ${svc.price}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setSelectedService(svc)}
                    className="w-7 h-7 rounded-md border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition-all"
                  >
                    <Eye
                      size={14}
                      className="text-gray-500"
                      strokeWidth={1.6}
                    />
                  </button>
                  <button
                    onClick={() => {
                      setSelectedService(svc);
                      setModal("edit");
                    }}
                    className="w-7 h-7 rounded-md border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition-all"
                  >
                    <Edit2
                      size={13}
                      className="text-gray-500"
                      strokeWidth={1.6}
                    />
                  </button>
                  <button
                    onClick={() => setDeleteTarget(svc)}
                    className="w-7 h-7 rounded-md bg-primary/10 flex items-center justify-center hover:bg-primary/20 transition-all"
                  >
                    <Trash2
                      size={14}
                      className="text-primary"
                      strokeWidth={1.6}
                    />
                  </button>
                </div>
              </div>
            ))}

            {/* Pagination footer */}
            <div className="flex items-center justify-between px-5 py-4">
              <span className="text-[12px] font-manrope-regular text-gray-500">
                Total{" "}
                <span className="font-manrope-bold text-gray-800">
                  {filtered.length}
                </span>{" "}
                services
              </span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  className="w-7 h-7 rounded-md flex items-center justify-center text-gray-400 hover:bg-gray-100 disabled:opacity-30 transition-all"
                >
                  <ChevronsLeft size={14} />
                </button>
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="w-7 h-7 rounded-md flex items-center justify-center text-gray-400 hover:bg-gray-100 disabled:opacity-30 transition-all"
                >
                  <ChevronLeft size={14} />
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(
                    (p) =>
                      p === 1 ||
                      p === totalPages ||
                      Math.abs(p - currentPage) <= 1,
                  )
                  .reduce((acc, p, idx, arr) => {
                    if (idx > 0 && arr[idx - 1] !== p - 1) acc.push("...");
                    acc.push(p);
                    return acc;
                  }, [])
                  .map((p, i) =>
                    p === "..." ? (
                      <span
                        key={`el-${i}`}
                        className="w-7 h-7 flex items-center justify-center text-[12px] text-gray-400"
                      >
                        …
                      </span>
                    ) : (
                      <button
                        key={p}
                        onClick={() => setCurrentPage(p)}
                        className={`w-7 h-7 rounded-md flex items-center justify-center text-[12px] font-manrope-semibold transition-all ${currentPage === p ? "bg-primary text-white" : "text-gray-500 hover:bg-gray-100"}`}
                      >
                        {p}
                      </button>
                    ),
                  )}
                <button
                  onClick={() =>
                    setCurrentPage((p) => Math.min(totalPages, p + 1))
                  }
                  disabled={currentPage === totalPages}
                  className="w-7 h-7 rounded-md flex items-center justify-center text-gray-400 hover:bg-gray-100 disabled:opacity-30 transition-all"
                >
                  <ChevronRight size={14} />
                </button>
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  className="w-7 h-7 rounded-md flex items-center justify-center text-gray-400 hover:bg-gray-100 disabled:opacity-30 transition-all"
                >
                  <ChevronsRight size={14} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {selectedService && (
        <ServiceDetailsPanel
          service={selectedService}
          onClose={() => setSelectedService(null)}
          onEdit={() => setModal("edit")}
          onDelete={() => setDeleteTarget(selectedService)}
        />
      )}

      {modal && (
        <ServiceModal
          mode={modal}
          service={selectedService}
          onClose={() => {
            setModal(null);
            if (modal === "add") setSelectedService(null);
          }}
        />
      )}

      {deleteTarget && (
        <DeleteModal
          service={deleteTarget}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={handleDelete}
          isDeleting={isDeleting}
        />
      )}
    </>
  );
}
